import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import emailSettingsApi from '@crema/services/apis/emailSettings';
import schedulerApi from '../../../../services/apis/schedulerApi';
import SchedulerToggleModal from '../../../../components/SchedulerToggleModal';
import { formatDateToJerusalem } from '../../../../utils/dateFormatter';

const SchedulerConfigurationForm = ({onSuccess, onError, currentSettings}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    scheduler_enabled: true,
    scheduler_cron: '59 59 23 * * *',
    scheduler_timezone: 'UTC',
  });
  const [errors, setErrors] = useState({});
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Common timezone options
  const timezones = [
    {value: 'UTC', label: 'UTC (GMT+0)'},

    // Europe & Middle East
    {value: 'Europe/London', label: 'London (GMT/BST)'},
    {value: 'Europe/Paris', label: 'Paris (CET - GMT+1)'},
    {value: 'Europe/Athens', label: 'Athens (EET - GMT+2)'},
    {value: 'Europe/Istanbul', label: 'Istanbul (TRT - GMT+3)'},
    {value: 'Europe/Moscow', label: 'Moscow (MSK - GMT+3)'},

    // Middle East & Africa (GMT+3)
    {value: 'Asia/Baghdad', label: 'Baghdad (AST - GMT+3)'},
    {value: 'Asia/Kuwait', label: 'Kuwait (AST - GMT+3)'},
    {value: 'Asia/Riyadh', label: 'Riyadh (AST - GMT+3)'},
    {value: 'Asia/Qatar', label: 'Qatar (AST - GMT+3)'},
    {value: 'Africa/Nairobi', label: 'Nairobi (EAT - GMT+3)'},
    {value: 'Asia/Dubai', label: 'Dubai (GST - GMT+4)'},

    // Asia
    {value: 'Asia/Karachi', label: 'Karachi (PKT - GMT+5)'},
    {value: 'Asia/Dhaka', label: 'Dhaka (BST - GMT+6)'},
    {value: 'Asia/Bangkok', label: 'Bangkok (ICT - GMT+7)'},
    {value: 'Asia/Shanghai', label: 'Shanghai (CST - GMT+8)'},
    {value: 'Asia/Tokyo', label: 'Tokyo (JST - GMT+9)'},

    // Americas
    {value: 'America/New_York', label: 'New York (EST - GMT-5)'},
    {value: 'America/Chicago', label: 'Chicago (CST - GMT-6)'},
    {value: 'America/Denver', label: 'Denver (MST - GMT-7)'},
    {value: 'America/Los_Angeles', label: 'Los Angeles (PST - GMT-8)'},

    // Australia & Pacific
    {value: 'Australia/Sydney', label: 'Sydney (AEDT - GMT+11)'},
    {value: 'Pacific/Auckland', label: 'Auckland (NZDT - GMT+13)'},
  ];

  // Common cron presets
  const cronPresets = [
    {
      value: '59 59 23 * * *',
      label: 'Daily at 11:59 PM',
      description: '23:59:59 every day',
    },
    {
      value: '0 0 0 * * *',
      label: 'Daily at Midnight',
      description: '00:00:00 every day',
    },
    {
      value: '0 0 12 * * *',
      label: 'Daily at Noon',
      description: '12:00:00 every day',
    },
    {
      value: '0 0 18 * * *',
      label: 'Daily at 6 PM',
      description: '18:00:00 every day',
    },
    {
      value: '0 0 * * * *',
      label: 'Every Hour',
      description: 'At the start of every hour',
    },
    {
      value: '0 */6 * * * *',
      label: 'Every 6 Hours',
      description: 'Every 6 hours',
    },
    {
      value: 'custom',
      label: 'Custom Cron Expression',
      description: 'Enter your own cron expression',
    },
  ];

  const [selectedPreset, setSelectedPreset] = useState('59 59 23 * * *');

  useEffect(() => {
    if (currentSettings) {
      const cronValue = currentSettings.scheduler_cron || '59 59 23 * * *';
      setFormData({
        scheduler_enabled:
          currentSettings.scheduler_enabled !== undefined
            ? Boolean(currentSettings.scheduler_enabled)
            : true,
        scheduler_cron: cronValue,
        scheduler_timezone: currentSettings.scheduler_timezone || 'UTC',
      });
      
      // Check if the cron value matches a preset, otherwise set to 'custom'
      const isPresetValue = cronPresets.some(preset => preset.value === cronValue);
      setSelectedPreset(isPresetValue ? cronValue : 'custom');
    }
    
    // Fetch current scheduler status
    fetchSchedulerStatus();
    
    // Cleanup function to prevent memory leaks
    return () => {
      setToggleLoading(false);
      setShowToggleModal(false);
    };
  }, [currentSettings]);

  // Fetch scheduler status from API
  const fetchSchedulerStatus = async () => {
    try {
      const response = await schedulerApi.getStatus();
      console.log('Scheduler status from API:', response.data);
      setSchedulerStatus(response.data);
      
      // Update form data to match actual scheduler status
      const newEnabledState = Boolean(response.data.isScheduled);
      console.log('Updating form data scheduler_enabled to:', newEnabledState);
      setFormData(prev => ({
        ...prev,
        scheduler_enabled: newEnabledState
      }));
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const validateCronExpression = (cronExpr) => {
    // Basic validation for cron expression (6 parts for node-cron)
    const parts = cronExpr.trim().split(/\s+/);
    if (parts.length !== 6) {
      return 'Cron expression must have 6 parts (second minute hour day month dayOfWeek)';
    }
    return null;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({...prev, [field]: value}));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: null}));
    }
    
    // If cron expression is changed manually, update selectedPreset to 'custom'
    if (field === 'scheduler_cron') {
      const isPresetValue = cronPresets.some(preset => preset.value === value);
      if (!isPresetValue) {
        setSelectedPreset('custom');
      }
    }
  };

  // Handle scheduler toggle with confirmation
  const handleSchedulerToggle = (newValue) => {
    if (newValue !== formData.scheduler_enabled) {
      setShowToggleModal(true);
      // DO NOT update formData.scheduler_enabled here
      // The modal will use the current formData.scheduler_enabled to determine its message
    } else {
      // If trying to set the same value, just update the form
      handleChange('scheduler_enabled', newValue);
    }
  };

  // Confirm scheduler toggle
  const handleConfirmToggle = async () => {
    try {
      setToggleLoading(true);
      
      console.log('Current form state:', formData.scheduler_enabled);
      
      // Call the appropriate API endpoint based on the current form state
      let response;
      if (formData.scheduler_enabled) {
        // Form shows enabled (scheduler is ON), so stop the scheduler
        console.log('Calling stop API...');
        response = await schedulerApi.stop();
      } else {
        // Form shows disabled (scheduler is OFF), so start the scheduler
        console.log('Calling start API...');
        response = await schedulerApi.start();
      }
      
      console.log('API response:', response);
      
      if (response.status === 200) {
        // Update scheduler status from API response
        setSchedulerStatus(response.data.config);
        
        // Update form data to match the actual scheduler status
        setFormData(prev => ({
          ...prev,
          scheduler_enabled: Boolean(response.data.config.isScheduled)
        }));
        
        // Refresh scheduler status to ensure UI is up to date
        await fetchSchedulerStatus();
        
        // Show success message
        onSuccess(response.data.message || 'Scheduler status updated successfully');
      } else {
        onError(response.data.message || 'Failed to update scheduler status');
      }
    } catch (error) {
      console.error('Error toggling scheduler:', error);
      onError(error.response?.data?.message || 'Failed to update scheduler status');
    } finally {
      setToggleLoading(false);
      setShowToggleModal(false);
    }
  };

  // Cancel toggle
  const handleCancelToggle = () => {
    // No need to revert form state since we didn't update it prematurely
    setShowToggleModal(false);
    setToggleLoading(false);
  };

  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    if (value !== 'custom') {
      handleChange('scheduler_cron', value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const newErrors = {};

    if (!formData.scheduler_cron) {
      newErrors.scheduler_cron = 'Cron expression is required';
    } else {
      const cronError = validateCronExpression(formData.scheduler_cron);
      if (cronError) {
        newErrors.scheduler_cron = cronError;
      }
    }

    if (!formData.scheduler_timezone) {
      newErrors.scheduler_timezone = 'Timezone is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSaving(true);

      // Update email settings with scheduler config
      const response = await emailSettingsApi.updateEmailSettings({
        ...currentSettings,
        scheduler_enabled: formData.scheduler_enabled,
        scheduler_cron: formData.scheduler_cron,
        scheduler_timezone: formData.scheduler_timezone,
      });

      if (response.data.status === 200) {
        onSuccess('Scheduler configuration updated successfully');
      } else {
        onError(
          response.data.message || 'Failed to update scheduler configuration',
        );
      }
    } catch (error) {
      console.error('Error updating scheduler configuration:', error);
      onError(
        error.response?.data?.message ||
          'Failed to update scheduler configuration',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box display='flex' alignItems='center' gap={1} mb={2}>
            <ScheduleIcon color='primary' />
            <Typography variant='h6'>Scheduler Configuration</Typography>
          </Box>

          <Alert severity='info' sx={{mb: 3}}>
            <Typography variant='body2'>
              Configure when the daily reports should be generated and sent
              automatically. The scheduler uses cron expressions to define the
              schedule.
            </Typography>
          </Alert>

          <Box component='form' onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Enable/Disable Scheduler */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.scheduler_enabled}
                      onChange={(e) =>
                        handleSchedulerToggle(e.target.checked)
                      }
                      color='primary'
                      disabled={toggleLoading}
                    />
                  }
                  label={
                    <Box display='flex' alignItems='center' gap={1}>
                      {formData.scheduler_enabled ? (
                        <PlayArrowIcon color='success' fontSize='small' />
                      ) : (
                        <StopIcon color='disabled' fontSize='small' />
                      )}
                      <Typography>
                        {formData.scheduler_enabled
                          ? 'Scheduler Enabled'
                          : 'Scheduler Disabled'}
                      </Typography>
                      {toggleLoading && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </Box>
                  }
                />
                {schedulerStatus && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Status: {schedulerStatus.isScheduled ? 'Running' : 'Stopped'} | 
                      Next Run: {schedulerStatus.nextRun} | 
                      Current Time: {formatDateToJerusalem(schedulerStatus.currentTime + (schedulerStatus.currentTime.includes('UTC') || schedulerStatus.currentTime.endsWith('Z') ? '' : ' UTC'))}

                    </Typography>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={fetchSchedulerStatus}
                      disabled={toggleLoading}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      Refresh
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Cron Preset Selector */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label='Schedule Preset'
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  helperText='Choose a common schedule or create a custom one'
                >
                  {cronPresets.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      <Box>
                        <Typography variant='body2'>{preset.label}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {preset.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Custom Cron Expression */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Cron Expression'
                  value={formData.scheduler_cron}
                  onChange={(e) => handleChange('scheduler_cron', e.target.value)}
                  error={!!errors.scheduler_cron}
                  helperText={
                    errors.scheduler_cron ||
                    'Format: second minute hour day month dayOfWeek (e.g., 59 59 23 * * *)'
                  }
                  disabled={!formData.scheduler_enabled}
                />
              </Grid>

              {/* Timezone */}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label='Timezone'
                  value={formData.scheduler_timezone}
                  onChange={(e) =>
                    handleChange('scheduler_timezone', e.target.value)
                  }
                  error={!!errors.scheduler_timezone}
                  helperText={
                    errors.scheduler_timezone ||
                    'Select the timezone for the schedule'
                  }
                  disabled={!formData.scheduler_enabled}
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Cron Expression Guide */}
              <Grid item xs={12}>
                <Divider sx={{my: 2}} />
                <Typography variant='subtitle2' gutterBottom>
                  Cron Expression Guide:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText
                      primary='Format: second minute hour day month dayOfWeek'
                      secondary='Each field can be: * (any), number (specific), */n (every n), or range (1-5)'
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon fontSize='small' color='primary' />
                    </ListItemIcon>
                    <ListItemText
                      primary='Examples:'
                      secondary='59 59 23 * * * = 23:59:59 every day | 0 0 12 * * * = 12:00:00 every day | 0 */30 * * * * = Every 30 minutes'
                    />
                  </ListItem>
                </List>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box display='flex' gap={2} justifyContent='flex-end'>
                  <Button
                    type='submit'
                    variant='contained'
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={saving || !formData.scheduler_enabled}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Scheduler Toggle Confirmation Modal */}
      <SchedulerToggleModal
        open={showToggleModal}
        onClose={handleCancelToggle}
        onConfirm={handleConfirmToggle}
        isEnabled={formData.scheduler_enabled}
        loading={toggleLoading}
      />
    </>
  );
};

SchedulerConfigurationForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  currentSettings: PropTypes.object,
};

export default SchedulerConfigurationForm;
